import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Loader2, Trash2, CalendarCheck, Mail, Lock } from "lucide-react";
import { LogoMark } from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const api = axios.create({ baseURL: API, withCredentials: true });

const STATUS_STYLES = {
  nuova: "bg-amber-400/15 text-amber-300 border-amber-400/30",
  confermata: "bg-green-500/15 text-green-400 border-green-500/30",
  completata: "bg-slate-500/15 text-slate-400 border-slate-500/30",
};

function formatDetail(detail) {
  if (detail == null) return "Errore. Riprova.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && e.msg) || "").filter(Boolean).join(" ");
  return String(detail);
}

const LoginForm = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      onLogin(data);
    } catch (err) {
      setError(formatDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center px-5">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-md rounded-3xl border border-white/10 bg-[#101017] p-10"
      >
        <div className="mb-8 flex flex-col items-center">
          <LogoMark size={52} />
          <h1 className="mt-5 font-display text-2xl font-extrabold text-white">
            Area Riservata
          </h1>
          <p className="mt-2 text-sm text-slate-500">Bacheca prenotazioni Taxi Bellinzona</p>
        </div>
        <label className="block">
          <span className="mb-2 block font-mono-alt text-[11px] uppercase tracking-[0.25em] text-slate-500">Email</span>
          <input
            data-testid="admin-login-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded-xl border border-white/15 bg-[#070709] px-4 py-3.5 text-sm text-white outline-none focus:border-amber-400"
          />
        </label>
        <label className="mt-5 block">
          <span className="mb-2 block font-mono-alt text-[11px] uppercase tracking-[0.25em] text-slate-500">Password</span>
          <input
            data-testid="admin-login-password"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded-xl border border-white/15 bg-[#070709] px-4 py-3.5 text-sm text-white outline-none focus:border-amber-400"
          />
        </label>
        {error && (
          <p data-testid="admin-login-error" className="mt-4 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-400">
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={loading}
          data-testid="admin-login-submit"
          className="mt-7 flex w-full items-center justify-center gap-2 rounded-full bg-amber-400 py-4 font-display text-sm font-extrabold uppercase tracking-wide text-black transition-colors hover:bg-amber-300 disabled:opacity-60"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
          Accedi
        </button>
        <a href="/" className="mt-5 block text-center text-xs text-slate-500 hover:text-amber-300">
          ← Torna al sito
        </a>
      </form>
    </div>
  );
};

const Dashboard = ({ user, onLogout }) => {
  const [tab, setTab] = useState("bookings");
  const [bookings, setBookings] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [b, c] = await Promise.all([api.get("/booking"), api.get("/contact")]);
      setBookings(b.data.sort((x, y) => (x.created_at < y.created_at ? 1 : -1)));
      setContacts(c.data.sort((x, y) => (x.created_at < y.created_at ? 1 : -1)));
    } catch {
      toast.error("Errore nel caricamento dei dati");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/booking/${id}`, { status });
      setBookings((prev) => prev.map((b) => (b.id === id ? { ...b, status } : b)));
      toast.success("Stato aggiornato");
    } catch {
      toast.error("Aggiornamento non riuscito");
    }
  };

  const remove = async (kind, id) => {
    if (!window.confirm("Eliminare definitivamente?")) return;
    try {
      await api.delete(`/${kind}/${id}`);
      if (kind === "booking") setBookings((prev) => prev.filter((b) => b.id !== id));
      else setContacts((prev) => prev.filter((c) => c.id !== id));
      toast.success("Eliminato");
    } catch {
      toast.error("Eliminazione non riuscita");
    }
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    onLogout();
  };

  return (
    <div className="min-h-screen px-5 py-10 sm:px-8" data-testid="admin-dashboard">
      <div className="mx-auto max-w-6xl">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <LogoMark size={40} />
            <div>
              <h1 className="font-display text-xl font-extrabold text-white">Bacheca Prenotazioni</h1>
              <p className="text-xs text-slate-500">{user.email}</p>
            </div>
          </div>
          <div className="flex gap-3">
            <a href="/" className="rounded-full border border-white/15 px-5 py-2.5 text-sm text-slate-300 hover:border-amber-400/50 hover:text-amber-300">
              Vai al sito
            </a>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="flex items-center gap-2 rounded-full border border-red-500/30 px-5 py-2.5 text-sm text-red-400 hover:bg-red-500/10"
            >
              <LogOut className="h-4 w-4" /> Esci
            </button>
          </div>
        </div>

        <div className="mt-10 flex gap-3">
          <button
            data-testid="admin-tab-bookings"
            onClick={() => setTab("bookings")}
            className={`flex items-center gap-2 rounded-full px-6 py-3 font-display text-sm font-bold transition-colors ${
              tab === "bookings" ? "bg-amber-400 text-black" : "border border-white/15 text-slate-300 hover:text-amber-300"
            }`}
          >
            <CalendarCheck className="h-4 w-4" /> Prenotazioni ({bookings.length})
          </button>
          <button
            data-testid="admin-tab-contacts"
            onClick={() => setTab("contacts")}
            className={`flex items-center gap-2 rounded-full px-6 py-3 font-display text-sm font-bold transition-colors ${
              tab === "contacts" ? "bg-amber-400 text-black" : "border border-white/15 text-slate-300 hover:text-amber-300"
            }`}
          >
            <Mail className="h-4 w-4" /> Richieste ({contacts.length})
          </button>
        </div>

        {loading ? (
          <div className="mt-16 flex justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-amber-400" />
          </div>
        ) : tab === "bookings" ? (
          <div className="mt-8 space-y-4" data-testid="admin-bookings-list">
            {bookings.length === 0 && <p className="text-slate-500">Nessuna prenotazione.</p>}
            {bookings.map((b) => (
              <div key={b.id} data-testid={`admin-booking-${b.id}`} className="rounded-2xl border border-white/10 bg-[#101017] p-6">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-3">
                      <span className="font-display text-lg font-bold text-white">{b.name}</span>
                      <span className={`rounded-full border px-3 py-1 font-mono-alt text-[10px] uppercase tracking-widest ${STATUS_STYLES[b.status || "nuova"]}`}>
                        {b.status || "nuova"}
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-amber-300">
                      {b.date} ore {b.time} — {b.origin} → {b.destination}
                    </p>
                    <p className="mt-1 text-xs text-slate-500">
                      {b.phone} • {b.email} • {b.passengers} passeggeri
                    </p>
                    {b.notes && <p className="mt-2 text-sm text-slate-400">Note: {b.notes}</p>}
                  </div>
                  <div className="flex items-center gap-2">
                    <select
                      data-testid={`booking-status-${b.id}`}
                      value={b.status || "nuova"}
                      onChange={(e) => setStatus(b.id, e.target.value)}
                      className="rounded-xl border border-white/15 bg-[#070709] px-3 py-2 text-sm text-white outline-none focus:border-amber-400"
                    >
                      <option value="nuova">Nuova</option>
                      <option value="confermata">Confermata</option>
                      <option value="completata">Completata</option>
                    </select>
                    <button
                      data-testid={`booking-delete-${b.id}`}
                      onClick={() => remove("booking", b.id)}
                      aria-label="Elimina prenotazione"
                      className="rounded-xl border border-red-500/30 p-2.5 text-red-400 hover:bg-red-500/10"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-8 space-y-4" data-testid="admin-contacts-list">
            {contacts.length === 0 && <p className="text-slate-500">Nessuna richiesta.</p>}
            {contacts.map((c) => (
              <div key={c.id} data-testid={`admin-contact-${c.id}`} className="rounded-2xl border border-white/10 bg-[#101017] p-6">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <span className="font-display text-lg font-bold text-white">{c.name}</span>
                    <p className="mt-1 text-sm text-amber-300">{c.subject}</p>
                    <p className="mt-1 text-xs text-slate-500">{c.phone} • {c.email}</p>
                    <p className="mt-2 text-sm text-slate-400">{c.message}</p>
                  </div>
                  <button
                    data-testid={`contact-delete-${c.id}`}
                    onClick={() => remove("contact", c.id)}
                    aria-label="Elimina richiesta"
                    className="rounded-xl border border-red-500/30 p-2.5 text-red-400 hover:bg-red-500/10"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default function Admin() {
  const [user, setUser] = useState(null);

  useEffect(() => {
    api.get("/auth/me")
      .then(({ data }) => setUser(data))
      .catch(() => setUser(false));
  }, []);

  if (user === null) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-amber-400" />
      </div>
    );
  }
  if (user === false) return <LoginForm onLogin={setUser} />;
  return <Dashboard user={user} onLogout={() => setUser(false)} />;
}
